import React, { useState, useRef, useEffect } from 'react';
import './TemplateEditor.css';

/**
 * TemplateEditor - Editor de templates com tags clicáveis e não editáveis
 * 
 * @param {string} value - Valor atual do template
 * @param {function} onChange - Callback quando o valor muda
 * @param {array} availableTags - Array de tags disponíveis [{ tag: '{nome}', label: 'Nome', desc: 'Nome do utilizador' }]
 * @param {string} placeholder - Placeholder do textarea
 * @param {string} type - Tipo do template (sms/email)
 */
const TemplateEditor = ({ value, onChange, availableTags = [], placeholder = '', type = 'sms' }) => {
  const [segments, setSegments] = useState([]);
  const [activeTagMenu, setActiveTagMenu] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const editorRef = useRef(null);

  // Parse template string into segments (text and tags)
  useEffect(() => {
    if (!value) {
      setSegments([{ type: 'text', content: '', id: 0 }]);
      return;
    }

    const parsed = [];
    let currentText = '';
    let id = 0;
    
    // Regex para encontrar tags {xxx}
    const tagRegex = /\{[a-zA-Z0-9_]+\}/g;
    let lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(value)) !== null) {
      // Adicionar texto antes da tag
      if (match.index > lastIndex) {
        const textBefore = value.substring(lastIndex, match.index);
        if (textBefore) {
          parsed.push({ type: 'text', content: textBefore, id: id++ });
        }
      }

      // Adicionar tag
      parsed.push({ type: 'tag', content: match[0], id: id++ });
      lastIndex = match.index + match[0].length;
    }

    // Adicionar texto restante
    if (lastIndex < value.length) {
      parsed.push({ type: 'text', content: value.substring(lastIndex), id: id++ });
    }

    // Se vazio, inicializar com texto vazio
    if (parsed.length === 0) {
      parsed.push({ type: 'text', content: '', id: 0 });
    }

    setSegments(parsed);
  }, [value]);

  // Update parent with current template value
  const updateValue = (newSegments) => {
    const newValue = newSegments.map(s => s.content).join('');
    onChange(newValue);
  };

  // Handle text input
  const handleTextChange = (segmentId, newContent) => {
    const newSegments = segments.map(seg => 
      seg.id === segmentId ? { ...seg, content: newContent } : seg
    );
    setSegments(newSegments);
    updateValue(newSegments);
  };

  // Insert tag at cursor position
  const insertTag = (tag) => {
    const focusedInput = document.querySelector('.template-editor-segment.focused');
    
    if (focusedInput && focusedInput.dataset.segmentId) {
      const segmentId = parseInt(focusedInput.dataset.segmentId);
      const segment = segments.find(s => s.id === segmentId);
      
      if (segment && segment.type === 'text') {
        const cursorPosition = focusedInput.selectionStart || segment.content.length;
        
        // Split texto no cursor e inserir tag
        const textBefore = segment.content.substring(0, cursorPosition);
        const textAfter = segment.content.substring(cursorPosition);
        
        const newSegments = [];
        for (const seg of segments) {
          if (seg.id === segmentId) {
            if (textBefore) newSegments.push({ type: 'text', content: textBefore, id: Date.now() + Math.random() });
            newSegments.push({ type: 'tag', content: tag, id: Date.now() + Math.random() + 1 });
            if (textAfter) newSegments.push({ type: 'text', content: textAfter, id: Date.now() + Math.random() + 2 });
          } else {
            newSegments.push(seg);
          }
        }
        
        setSegments(newSegments);
        updateValue(newSegments);
      }
    } else {
      // Se não há foco, adicionar no final
      const newSegments = [...segments];
      if (newSegments[newSegments.length - 1]?.type === 'text' && newSegments[newSegments.length - 1].content === '') {
        newSegments[newSegments.length - 1] = { type: 'tag', content: tag, id: Date.now() };
        newSegments.push({ type: 'text', content: '', id: Date.now() + 1 });
      } else {
        newSegments.push({ type: 'tag', content: tag, id: Date.now() });
        newSegments.push({ type: 'text', content: '', id: Date.now() + 1 });
      }
      setSegments(newSegments);
      updateValue(newSegments);
    }
    
    setActiveTagMenu(false);
  };

  // Remove tag
  const removeTag = (segmentId) => {
    const segmentIndex = segments.findIndex(s => s.id === segmentId);
    if (segmentIndex === -1) return;

    const newSegments = [...segments];
    newSegments.splice(segmentIndex, 1);

    // Merge adjacent text segments
    for (let i = 0; i < newSegments.length - 1; i++) {
      if (newSegments[i].type === 'text' && newSegments[i + 1]?.type === 'text') {
        newSegments[i] = {
          ...newSegments[i],
          content: newSegments[i].content + newSegments[i + 1].content
        };
        newSegments.splice(i + 1, 1);
        i--;
      }
    }

    // Garantir pelo menos um segmento de texto
    if (newSegments.length === 0 || newSegments.every(s => s.type === 'tag')) {
      newSegments.push({ type: 'text', content: '', id: Date.now() });
    }

    setSegments(newSegments);
    updateValue(newSegments);
  };

  // Handle backspace/delete in empty text segment
  const handleKeyDown = (e, segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || segment.type !== 'text') return;

    const input = e.target;
    const cursorAtStart = input.selectionStart === 0;
    const cursorAtEnd = input.selectionStart === segment.content.length;

    // Backspace no início - remover tag anterior
    if (e.key === 'Backspace' && cursorAtStart && segment.content === '') {
      const segmentIndex = segments.findIndex(s => s.id === segmentId);
      if (segmentIndex > 0 && segments[segmentIndex - 1].type === 'tag') {
        e.preventDefault();
        removeTag(segments[segmentIndex - 1].id);
      }
    }

    // Delete no final - remover tag seguinte
    if (e.key === 'Delete' && cursorAtEnd && segment.content === '') {
      const segmentIndex = segments.findIndex(s => s.id === segmentId);
      if (segmentIndex < segments.length - 1 && segments[segmentIndex + 1].type === 'tag') {
        e.preventDefault();
        removeTag(segments[segmentIndex + 1].id);
      }
    }
  };

  return (
    <div className="template-editor">
      <div className="template-editor-container" ref={editorRef}>
        {segments.map((segment) => (
          <React.Fragment key={segment.id}>
            {segment.type === 'text' ? (
              <input
                type="text"
                className="template-editor-segment"
                data-segment-id={segment.id}
                value={segment.content}
                onChange={(e) => handleTextChange(segment.id, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, segment.id)}
                onFocus={(e) => e.target.classList.add('focused')}
                onBlur={(e) => e.target.classList.remove('focused')}
                placeholder={segments.length === 1 ? placeholder : ''}
                style={{ 
                  width: segment.content ? `${Math.max(segment.content.length * 8 + 10, 50)}px` : '100px',
                  minWidth: segments.length === 1 ? '100%' : '50px'
                }}
              />
            ) : (
              <span className="template-tag">
                {segment.content}
                <button
                  type="button"
                  className="template-tag-remove"
                  onClick={() => removeTag(segment.id)}
                  title="Remover tag"
                >
                  ×
                </button>
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Tags disponíveis */}
      <div className="template-tags-panel">
        <div className="template-tags-header">
          <span className="template-tags-title">🏷️ Tags Disponíveis</span>
          <span className="template-tags-subtitle">Clique para inserir</span>
        </div>
        <div className="template-tags-grid">
          {availableTags.map((tagInfo, idx) => (
            <button
              key={idx}
              type="button"
              className="template-tag-button"
              onClick={() => insertTag(tagInfo.tag)}
              title={tagInfo.desc}
            >
              <span className="template-tag-button-tag">{tagInfo.tag}</span>
              <span className="template-tag-button-label">{tagInfo.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Character count for SMS */}
      {type === 'sms' && (
        <div className="template-char-count">
          {value?.length || 0} / 160 caracteres
          {value && value.length > 160 && (
            <span className="template-char-warning"> (Será enviado em {Math.ceil(value.length / 160)} SMS)</span>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateEditor;
